import React from 'react'
import styled, { DefaultTheme, ThemeProps } from 'styled-components'
import theme from 'styles/theme'

const COLORS_BY_COLOR_PROP = {
  teal: {
    textColor: theme.colors.keppel,
    backgroundColor: theme.colors.polar,
  },
  orange: {
    textColor: theme.colors.tawny,
    backgroundColor: theme.colors.bridesmaid,
  },
  indigo: {
    textColor: theme.colors.indigo,
    backgroundColor: theme.colors.bluechark,
  },
  gray: {
    textColor: theme.colors.text.dark.secondary,
    backgroundColor: theme.colors.gallery,
  },
  none: {
    textColor: 'none',
    backgroundColor: 'none',
  },
} as const

type PillColors = keyof typeof COLORS_BY_COLOR_PROP

type Primary = {
  kind: 'primary'
}

type Secondary = {
  kind: 'secondary'
}

type Special = {
  kind: 'special'
  textTint: string
  backgroundTint: string
}

type Error = {
  kind: 'error'
  backgroundTint: string
}

export type Variant = Primary | Secondary | Special | Error

const getColor = ({ $variant, theme }: StyledPillProps) => {
  switch ($variant.kind) {
    case 'primary':
      return theme.colors.foreground.primary[1]
    case 'secondary':
      return theme.colors.foreground.secondary[2]
    case 'special':
      return $variant.textTint
    case 'error':
      return theme.colors.foreground.primary[1]
  }
}

const getBackgroundColor = ({ $variant, theme }: StyledPillProps) => {
  switch ($variant.kind) {
    case 'primary':
      return theme.colors.primaryTint[100]
    case 'secondary':
      return theme.colors.background.tertiary[2]
    case 'special':
      return $variant.backgroundTint
    case 'error':
      return theme.colors.state.negative
  }
}

interface StyledPillProps extends ThemeProps<DefaultTheme> {
  $color?: PillColors
  $variant: Variant
}

const StyledPill = styled.span<StyledPillProps>`
  height: 18px;
  display: inline-flex;
  align-items: center;
  text-transform: capitalize;

  font-size: ${({ theme }) => theme.fontSizes.tiny};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: 2;
  white-space: nowrap;

  padding: 0px 8px;
  border-radius: ${({ theme }) => theme.radii.xxlarge};

  color: ${props => getColor(props)};
  background-color: ${props => getBackgroundColor(props)};
`

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: Variant
}

const Pill: React.FC<PillProps> = ({
  variant = { kind: 'primary' },
  children,
  className,
  ...props
}) => (
  <StyledPill className={className} $variant={variant} {...props}>
    {children}
  </StyledPill>
)

export default styled(Pill)``
